import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, type StaffRole } from "../lib/api.ts";
import { useSession } from "../lib/auth.ts";
import { useStaff } from "../lib/staff.ts";
import { useSortableTable, SortHeader } from "../components/SortableTable.tsx";

export function StaffUsersPage() {
  const qc = useQueryClient();
  const { data: session } = useSession();
  const myUserId = session?.user?.id;
  // All mutations (create / delete staff) are admin-only on the server.
  const { role: myRole } = useStaff();
  const canMutate = myRole === "admin";
  const list = useQuery({
    queryKey: ["staff", "users"],
    queryFn: api.staffListUsers,
  });
  const sortable = useSortableTable(list.data?.staff ?? [], {
    email:     (s) => s.email,
    name:      (s) => s.name,
    role:      (s) => s.role,
    createdAt: (s) => s.createdAt,
  }, "createdAt", "desc");

  const [email, setEmail] = useState("");
  const [role, setRole] = useState<StaffRole>("support");
  const [err, setErr] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => api.staffCreateUser(email.trim(), role),
    onSuccess: () => {
      setEmail("");
      setErr(null);
      qc.invalidateQueries({ queryKey: ["staff", "users"] });
    },
    onError: (e: Error) => setErr(e.message),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.staffDeleteUser(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["staff", "users"] }),
    onError: (e: Error) => setErr(e.message),
  });

  return (
    <>
      <h1>Staff users</h1>
      <p className="muted">
        Promote registered users to maker-staff. The target user must sign up
        first; their role takes effect on their next request.
      </p>

      {canMutate && (
      <section style={{ display: "grid", gap: "0.5rem", maxWidth: 480, marginBottom: "1.5rem" }}>
        <h2 style={{ marginBottom: 0 }}>Add staff</h2>
        <input
          type="email"
          placeholder="email@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          aria-label="User email"
        />
        <select
          value={role}
          onChange={(e) => setRole(e.target.value as StaffRole)}
          aria-label="Role"
        >
          <option value="support">support</option>
          <option value="developer">developer</option>
          <option value="admin">admin</option>
        </select>
        <button
          disabled={!email.trim() || create.isPending}
          onClick={() => create.mutate()}
        >
          {create.isPending ? "Adding…" : "Add staff user"}
        </button>
        {err && <p className="err">{err}</p>}
      </section>
      )}

      {list.isLoading && <p className="muted">Loading…</p>}
      {list.error && <p className="err">Couldn't load — {(list.error as Error).message}</p>}
      {list.data && (
        <table>
          <thead>
            <tr>
              <SortHeader label="Email" columnKey="email"     sortKey={sortable.sortKey} sortDir={sortable.sortDir} onSort={sortable.handleSort} />
              <SortHeader label="Name"  columnKey="name"      sortKey={sortable.sortKey} sortDir={sortable.sortDir} onSort={sortable.handleSort} />
              <SortHeader label="Role"  columnKey="role"      sortKey={sortable.sortKey} sortDir={sortable.sortDir} onSort={sortable.handleSort} />
              <SortHeader label="Added" columnKey="createdAt" sortKey={sortable.sortKey} sortDir={sortable.sortDir} onSort={sortable.handleSort} />
              <th></th>
            </tr>
          </thead>
          <tbody>
            {sortable.sorted.map((s) => (
              <tr key={s.id}>
                <td>{s.email ?? <span className="muted">(deleted user)</span>}</td>
                <td>{s.name ?? <span className="muted">—</span>}</td>
                <td>{s.role}</td>
                <td>{new Date(s.createdAt).toLocaleDateString()}</td>
                <td>
                  {s.userId === myUserId ? (
                    <span className="muted">you</span>
                  ) : canMutate ? (
                    <button
                      className="secondary"
                      onClick={() => remove.mutate(s.id)}
                      disabled={remove.isPending}
                    >
                      Remove
                    </button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}
